import { useState, type FormEvent } from "react";
import {
  ArrowRight,
  KeyRound,
  LockKeyhole,
  Mail,
  ShoppingBag,
} from "lucide-react";
import { Button, Field } from "../components/UI";
import { supabase } from "../lib/supabase";

const ACCOUNT_EMAIL = "laise.welove@gmail.com";
type LoginMode = "password" | "magic" | "reset";

export function Login() {
  const [email, setEmail] = useState(ACCOUNT_EMAIL);
  const [password, setPassword] = useState("");
  const [sent, setSent] = useState(false);
  const [mode, setMode] = useState<LoginMode>("password");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    const redirectTo = mode === "reset"
      ? `${window.location.origin}/?recovery=1`
      : window.location.origin;
    const { error: authError } =
      mode === "password"
        ? await supabase().auth.signInWithPassword({ email, password })
        : mode === "reset"
          ? await supabase().auth.resetPasswordForEmail(email, { redirectTo })
          : await supabase().auth.signInWithOtp({
              email,
              options: { emailRedirectTo: redirectTo },
            });
    setLoading(false);
    if (authError) {
      setError(
        mode === "password" && authError.message === "Invalid login credentials"
          ? "E-mail ou senha incorretos."
          : authError.message,
      );
    } else if (mode !== "password") {
      setSent(true);
    }
  };
  const selectMode = (nextMode: LoginMode) => {
    setMode(nextMode);
    setSent(false);
    setError("");
    setPassword("");
  };
  return (
    <main className="auth-page">
      <section className="auth-brand">
        <div className="brand-lockup brand-lockup--large">
          <span className="brand-mark">
            <ShoppingBag size={25} />
          </span>
          <div>
            <strong>WeSell</strong>
            <small>afiliados com clareza</small>
          </div>
        </div>
        <div>
          <p className="eyebrow">Seu QG de afiliações</p>
          <h1>Dos achadinhos à comissão, sem planilha espalhada.</h1>
          <p>Produtos, códigos, conteúdos, cliques e vendas em uma visão só.</p>
        </div>
        <span className="auth-note">
          <LockKeyhole size={18} /> Acesso privado e protegido
        </span>
      </section>
      <section className="auth-card">
        <div>
          <p className="eyebrow">{mode === "reset" ? "Recuperar acesso" : "Entrar"}</p>
          <h2>
            {sent
              ? "Confira seu e-mail"
              : mode === "reset"
                ? "Crie uma nova senha"
                : "Bem-vinda de volta"}
          </h2>
          <p>
            {sent
              ? `Enviamos as instruções para ${email}.`
              : mode === "reset"
                ? "Digite seu e-mail para receber um link de redefinição."
                : mode === "magic"
                  ? "Receba um link seguro no e-mail, sem digitar sua senha."
                  : "Entre com sua conta privada do WeSell."}
          </p>
        </div>
        {sent ? (
          <Button variant="secondary" onClick={() => setSent(false)}>
            Usar outro e-mail
          </Button>
        ) : (
          <form className="form-grid" onSubmit={submit}>
            <Field label="E-mail">
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
                autoFocus
              />
            </Field>
            {mode === "password" ? (
              <Field label="Senha">
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
              </Field>
            ) : null}
            {error ? (
              <p className="form-error" role="alert">
                {error}
              </p>
            ) : null}
            <Button type="submit" loading={loading}>
              {mode === "password"
                ? "Entrar no WeSell"
                : mode === "reset"
                  ? "Enviar redefinição"
                  : "Enviar link de acesso"}{" "}
              <ArrowRight size={17} />
            </Button>
          </form>
        )}
        <div className="auth-card__alternatives">
          {mode !== "password" ? (
            <button
              type="button"
              className="text-button"
              onClick={() => selectMode("password")}
            >
              <KeyRound size={15} /> Entrar com senha
            </button>
          ) : null}
          {mode !== "magic" ? (
            <button
              type="button"
              className="text-button"
              onClick={() => selectMode("magic")}
            >
              <Mail size={15} /> Receber link de acesso
            </button>
          ) : null}
          {mode !== "reset" ? (
            <button
              type="button"
              className="text-button"
              onClick={() => selectMode("reset")}
            >
              Redefinir minha senha
            </button>
          ) : null}
        </div>
        <a href="/?demo=1" className="text-link">
          Explorar demonstração
        </a>
      </section>
    </main>
  );
}

export function PasswordRecovery() {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password.length < 10) {
      setError("Use pelo menos 10 caracteres.");
      return;
    }
    if (password !== confirmation) {
      setError("As senhas não coincidem.");
      return;
    }
    setLoading(true);
    setError("");
    const { error: updateError } = await supabase().auth.updateUser({
      password,
    });
    setLoading(false);
    if (updateError) setError(updateError.message);
    else window.location.href = "/";
  };
  return (
    <main className="auth-page auth-page--single">
      <section className="auth-card">
        <span className="metric-card__icon">
          <KeyRound size={22} />
        </span>
        <div>
          <p className="eyebrow">Recuperar acesso</p>
          <h1>Defina sua nova senha</h1>
        </div>
        <form className="form-grid" onSubmit={submit}>
          <Field label="Nova senha">
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="new-password"
              required
              autoFocus
            />
          </Field>
          <Field label="Confirmar senha">
            <input
              type="password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="new-password"
              required
            />
          </Field>
          {error ? (
            <p className="form-error" role="alert">
              {error}
            </p>
          ) : null}
          <Button type="submit" loading={loading}>
            Salvar nova senha
          </Button>
        </form>
      </section>
    </main>
  );
}
